import React from 'react';

import { Link } from '@eventbrite/eds-link';
import { makeValidator } from '@eventbrite/eds-utils';
import { gettext } from '@eventbrite/i18n';
import {
    isProvided,
    makeMatchesRegex,
    isAnEmail,
} from '@eventbrite/validators';
import { TYPE_ERROR, TYPE_SUCCESS } from '@eventbrite/eds-notification';

import {
    CREDIT_CARD_REGEX,
    NEW_REFUND_SUCCESS,
    REFUND_ALREADY_EXISTS,
    REFUND_VOIDED,
    REFUND_REQUEST_INVALID,
    REFUND_ORDER_NOT_FOUND,
    REFUND_ORDER_IS_FREE,
    REFUND_ORDER_IS_NOT_EPP,
    REFUND_COMPLETED,
    REFUND_ORDER_TRANSFERRED,
    REFUND_ORDER_EMAIL_DOES_NOT_MATCH,
    REFUND_ORDER_ABANDONED,
    REFUND_ORDER_GATED,
    REFUND_ORDER_DELETED,
    REFUND_CAPTCHA_ERROR,
    REFUND_CAPTCHA_FAILED,
    REFUND_CREDIT_CARD_DETECTED,
} from './constants';

import {
    REFUND_GENERIC_INVALID_ORDER_MESSAGE,
    REFUND_ORDER_EMAIL_DOES_NOT_MATCH_MESSAGE,
    REFUND_REQUEST_ALREADY_EXISTS_MESSAGE,
    REFUND_REQUEST_NOT_VALID_MESSAGE,
    REFUND_REQUEST_ORDER_ALREADY_REFUNDED_MESSAGE,
    REFUND_REQUEST_ORDER_ALREADY_VOIDED_MESSAGE,
    REFUND_REQUEST_ORDER_IS_FREE_MESSAGE,
    REFUND_REQUEST_ORDER_IS_NOT_EPP_MESSAGE,
    REFUND_REQUEST_ORDER_TRANSFERRED_MESSAGE,
    REFUND_REQUEST_SUCCESS_MESSAGE,
    REFUND_REQUEST_CAPTCHA_ERROR_MESSAGE,
    REFUND_REQUEST_CREDIT_CARD_DETECTED_MESSAGE,
} from './constants/messages';

export const validateCreditCardDataNotPresent = (value) =>
    // Remove every single space and '-' on the string before validating with the credit card regex
    value ? !CREDIT_CARD_REGEX.test(value.replace(/[ ]g|[- ]/g, '')) : true;

export const formValidate = makeValidator({
    attendeeEmail: [
        {
            validator: isProvided,
            errorMessage: gettext('Email is required'),
        },
        {
            validator: isAnEmail,
            errorMessage: gettext('Email is invalid'),
        },
    ],
    orderId: [
        {
            validator: isProvided,
            errorMessage: gettext('Order ID is required'),
        },
        {
            validator: makeMatchesRegex(/^[0-9]+$/),
            errorMessage: gettext('Order ID must be a number'),
        },
    ],
    additionalInformation: [
        {
            validator: validateCreditCardDataNotPresent,
            errorMessage: REFUND_REQUEST_CREDIT_CARD_DETECTED_MESSAGE,
        },
    ],
    captcha: [
        {
            validator: isProvided,
            errorMessage: gettext('reCAPTCHA is required'),
        },
    ],
});

export const MessageWithLearnMore = ({ text, link }) => (
    <div>
        {text}
        <Link to={link} style="inverse">
            {gettext('Learn more')}
        </Link>
    </div>
);

export const getRequestStatusSpec = ({
    canIGetARefund,
    whereAreMyTickets,
    whereIsMyRefund,
}) => {
    const REFUND_REQUEST_DEFAULT_ORDER_DECLINED_MESSAGE = (
        <MessageWithLearnMore
            text={REFUND_GENERIC_INVALID_ORDER_MESSAGE}
            link={whereAreMyTickets}
        />
    );

    const REFUND_REQUEST_ORDER_IS_FREE_MESSAGE_COMPONENT = (
        <MessageWithLearnMore
            text={REFUND_REQUEST_ORDER_IS_FREE_MESSAGE}
            link={whereAreMyTickets}
        />
    );

    return {
        [NEW_REFUND_SUCCESS]: {
            message: REFUND_REQUEST_SUCCESS_MESSAGE,
            type: TYPE_SUCCESS,
        },
        [REFUND_ALREADY_EXISTS]: {
            message: REFUND_REQUEST_ALREADY_EXISTS_MESSAGE,
            type: TYPE_SUCCESS,
        },
        [REFUND_REQUEST_INVALID]: {
            message: REFUND_REQUEST_NOT_VALID_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_NOT_FOUND]: {
            message: REFUND_REQUEST_DEFAULT_ORDER_DECLINED_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_IS_FREE]: {
            message: REFUND_REQUEST_ORDER_IS_FREE_MESSAGE_COMPONENT,
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_IS_NOT_EPP]: {
            message: (
                <MessageWithLearnMore
                    text={REFUND_REQUEST_ORDER_IS_NOT_EPP_MESSAGE}
                    link={canIGetARefund}
                />
            ),
            type: TYPE_ERROR,
        },
        [REFUND_COMPLETED]: {
            message: (
                <MessageWithLearnMore
                    text={REFUND_REQUEST_ORDER_ALREADY_REFUNDED_MESSAGE}
                    link={whereIsMyRefund}
                />
            ),
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_TRANSFERRED]: {
            message: (
                <MessageWithLearnMore
                    text={REFUND_REQUEST_ORDER_TRANSFERRED_MESSAGE}
                    link={whereAreMyTickets}
                />
            ),
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_EMAIL_DOES_NOT_MATCH]: {
            message: (
                <MessageWithLearnMore
                    text={REFUND_ORDER_EMAIL_DOES_NOT_MATCH_MESSAGE}
                    link={whereAreMyTickets}
                />
            ),
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_ABANDONED]: {
            message: REFUND_REQUEST_DEFAULT_ORDER_DECLINED_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_GATED]: {
            message: REFUND_REQUEST_DEFAULT_ORDER_DECLINED_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_ORDER_DELETED]: {
            message: REFUND_REQUEST_DEFAULT_ORDER_DECLINED_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_CAPTCHA_ERROR]: {
            message: REFUND_REQUEST_CAPTCHA_ERROR_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_CAPTCHA_FAILED]: {
            message: REFUND_REQUEST_CAPTCHA_ERROR_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_CREDIT_CARD_DETECTED]: {
            message: REFUND_REQUEST_CREDIT_CARD_DETECTED_MESSAGE,
            type: TYPE_ERROR,
        },
        [REFUND_VOIDED]: {
            message: REFUND_REQUEST_ORDER_ALREADY_VOIDED_MESSAGE,
            type: TYPE_ERROR,
        },
    };
};
